import type { PermissionMap, Policy } from '@dastarhan/contracts';

/**
 * Права, появившиеся позже первых заведений.
 *
 * Роль, созданная до введения права, ничего о нём не знает — и молча
 * запретить ей действие нельзя: касса встанет посреди смены у людей,
 * которые ничего не меняли. Отсутствие ключа здесь значит «вопрос
 * не задавали», а не «ответили нет».
 *
 * Владелец проставит право осознанно в разделе «Команда», и с этого
 * момента решает уже он.
 */
const ADDED_LATER: Record<string, Policy> = {
  // Введены вместе с правом на оплату
  'order.pay': 'allowed',

  /*
   * Введены при разборе прав по образцу большой системы. Роль, созданная
   * до них, о них не знает — и запрет остановил бы работу у людей,
   * которые ничего не меняли.
   *
   * Открываем то, что и так было доступно всем: стоп-лист, гости,
   * перенос столов, открытие смены. И НЕ ОТКРЫВАЕМ опасное — правку
   * закрытых чеков, склад, финансы, управление людьми: этого раньше
   * не было вовсе, и появиться оно должно по решению владельца.
   */
  'table.manage': 'allowed',
  'stoplist.manage': 'allowed',
  'guest.create': 'allowed',
  'pos.mode.switch': 'allowed',
  'cash.shift.open': 'allowed',
  'cash.drawer': 'allowed',
};

/**
 * Политика действия для этой роли.
 *
 * Общее правило строгое: нет ключа — нет права. Исключение только
 * для прав, введённых после того, как роль была создана.
 */
export function policyOf(map: PermissionMap, key: string): Policy {
  const set = map[key];
  if (set) return set;

  /*
   * Доступ к чужим столам до введения своего права прикрывался правом
   * снимать позиции. Открыть его всем «чтобы не сломать работу» значит
   * снять защиту у тех, кто её и не имел: официант получил бы чужие
   * столы, которых у него никогда не было.
   *
   * Поэтому старой роли даём ровно то, что у неё было: доступ к чужим
   * столам, если ей разрешено снимать позиции.
   */
  /*
   * Возврат счёта в работу: раньше отдельного права не было, и стол
   * фактически возвращал любой, кто мог править заказ. Наследуем
   * ту же мерку, чтобы никого не отрезать переходом
   */
  if (key === 'table.unbill') {
    return map['order.item.remove'] === 'allowed' ? 'allowed' : 'denied';
  }

  if (key === 'table.any') {
    return map['order.item.remove'] === 'allowed' ? 'allowed' : 'denied';
  }

  return ADDED_LATER[key] ?? 'denied';
}
